import {useCallback,useEffect,useId,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {useQuery} from '@tanstack/react-query';
import {Button,Card} from '@pocketbuy/outdoor-design-system';
import {X} from 'lucide-react';
import {skuApi} from '../../api';
import {useSession} from '../../session';
import {ImeiCameraScanner} from '../../components/ImeiCameraScanner';
import {isValidImei} from '../../components/imei';
import {useDialogScrollLock} from '../../components/useDialogScrollLock';
import './SkuSamplePhone.css';

export const samplePhoneHint='Scan to help identify this model. This won’t add the phone to stock.';
type Model={brand:string;marketModelName:string;ram:string;storage:string;variant:string;colour:string};

export function SkuSamplePhone({savedImei,pendingImei,onSelect,disabled,...model}:Model&{
 savedImei?:string|null;pendingImei?:string;onSelect:(imei:string|undefined)=>void;disabled:boolean;
}){
 const[open,setOpen]=useState(false);
 const close=useCallback(()=>setOpen(false),[]);
 const ready=Boolean(model.brand.trim()&&model.marketModelName.trim()&&/^[1-9]\d*$/.test(model.ram)&&/^[1-9]\d*$/.test(model.storage));
 return <Card className="pb-stack op-sku-sample">
  <span className="pb-field-label">Sample phone <span className="pb-muted">Optional</span></span>
  <p className="pb-field-hint">{samplePhoneHint}</p>
  {(pendingImei||savedImei)&&<div className="pb-stack op-sample-status" role="status"><strong>{pendingImei?'Sample phone selected':'Sample phone linked'}</strong><span className="op-imei-value">{pendingImei||savedImei}</span>{pendingImei&&<small>Save the model to link this sample.</small>}</div>}
  <Button type="button" className="secondary" disabled={disabled||!ready} onClick={()=>setOpen(true)}>{pendingImei||savedImei?'Scan another sample':'Scan sample phone'}</Button>
  {!ready&&<small className="pb-field-hint">Enter the brand, market model and memory sizes first.</small>}
  {pendingImei&&<button type="button" className="op-edit-imei-action" disabled={disabled} onClick={()=>onSelect(undefined)}>Discard sample change</button>}
  {open&&<SamplePhoneDialog {...model} onClose={close} onSelect={onSelect}/>}
 </Card>;
}

function SamplePhoneDialog({onClose,onSelect,...model}:Model&{onClose:()=>void;onSelect:(imei:string)=>void}){
 const dialog=useRef<HTMLDialogElement>(null);const[imei,setImei]=useState('');
 const token=useSession(s=>s.session?.accessToken);const overlayId=useId();const closing=useRef(false);
 useDialogScrollLock(dialog);
 const close=()=>{if(!closing.current){closing.current=true;window.history.back()}};
 useEffect(()=>{
  const focus=document.activeElement as HTMLElement|null;
  dialog.current?.showModal();
  if(window.history.state?.samplePhoneId!==overlayId)window.history.pushState({...window.history.state,samplePhoneId:overlayId},'');
  const pop=()=>onClose();window.addEventListener('popstate',pop);
  return()=>{window.removeEventListener('popstate',pop);focus?.focus({preventScroll:true})};
 },[onClose,overlayId]);
 const check=useQuery({queryKey:['sku-sample-check',token,imei,model.brand,model.marketModelName],queryFn:()=>skuApi.checkSample(imei,model.brand,model.marketModelName),enabled:isValidImei(imei),retry:false,staleTime:0,gcTime:0,refetchOnWindowFocus:false});
 const confirmed=check.isSuccess&&check.data.imei===imei&&!check.isFetching;
 const label=[`${model.brand.trim()} ${model.marketModelName.trim()}`,`${model.ram}GB + ${model.storage}GB`,model.variant.trim(),model.colour.trim()].filter(Boolean).join(' · ');
 return createPortal(<dialog ref={dialog} className="op-sample-dialog" aria-labelledby="sample-phone-title" onCancel={e=>{e.preventDefault();close()}}>
  <header><h2 id="sample-phone-title">Scan sample phone</h2><button type="button" className="pb-icon-button" aria-label="Close sample scan" onClick={close} autoFocus><X size={22}/></button></header>
  <div className="op-sample-dialog-body pb-stack">
   <p className="pb-field-hint">{samplePhoneHint}</p>
   <ImeiCameraScanner label="Sample IMEI" value={imei} onChange={setImei} confirmManual initialCameraOpen/>
   {isValidImei(imei)&&check.isFetching&&<p role="status">Checking sample phone…</p>}
   {isValidImei(imei)&&check.isError&&<div className="pb-stack"><p className="op-field-error" role="alert">{check.error instanceof Error?check.error.message:'The sample could not be checked. Try again.'}</p><Button type="button" className="secondary" onClick={()=>void check.refetch()}>Try again</Button></div>}
   {confirmed&&<div className="pb-stack"><p>Confirm that the sample matches this model and its specifications:</p><strong>{label}</strong></div>}
  </div>
  <footer><Button type="button" className="pb-full" disabled={!confirmed} onClick={()=>{onSelect(imei);close()}}>Link sample phone</Button></footer>
 </dialog>,document.body);
}
